import React, { useState, useMemo } from "react";
import { ButtonComp, CardComp } from "../../../../components";
import {
  useGetCompanyRegistrationFeesQuery,
  useImportRegistrationFeesMutation,
} from "../../../../store/admin/settings/regFeeSlice";
import toast from "react-hot-toast";
import { formatMoney } from "../../../../utils";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";

const ImportRegistrationFeesModal = ({ handleClose }) => {
  const [selectedFees, setSelectedFees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const { data, isLoading, isFetching } = useGetCompanyRegistrationFeesQuery();
  const [importFees, { isLoading: isImporting }] =
    useImportRegistrationFeesMutation();

  const companyFees = data || [];

  // Filter fees by search term
  const filteredFees = useMemo(() => {
    if (!searchTerm) return companyFees;
    return companyFees.filter(
      (fee) =>
        fee?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fee?.fee_category_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fee?.branch_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [companyFees, searchTerm]);

  const handleToggleSelection = (feeId) => {
    setSelectedFees((prev) => {
      if (prev.includes(feeId)) {
        return prev.filter((id) => id !== feeId);
      }
      return [...prev, feeId];
    });
  };

  const handleSelectAll = () => {
    if (selectedFees.length === filteredFees.length) {
      setSelectedFees([]);
    } else {
      setSelectedFees(filteredFees.map((fee) => fee.id));
    }
  };

  const handleImport = async () => {
    if (selectedFees.length === 0) {
      return toast.error("Please select at least one registration fee to import");
    }

    const response = await importFees({
      fee_ids: selectedFees,
    });

    const { data, error } = response;
    if (error) {
      return toast.error(error?.data?.message || "An error occurred");
    } else if (data?.status === false) {
      return toast.error(data?.message || "An error occurred");
    } else {
      toast.success(
        data?.message ||
          `Successfully imported ${selectedFees.length} registration fee(s)`
      );
      handleClose();
    }
  };

  const isAllSelected =
    filteredFees.length > 0 &&
    selectedFees.length === filteredFees.length;

  return (
    <div className="py-3">
      <div className="mb-4">
        <h5 className="fw-bold mb-2 text-black1">Import Registration Fees</h5>
        <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
          Select registration fees from other branches in your company to import into
          your current branch
        </p>
      </div>

      {/* Search Input */}
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search registration fees..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: "10px 15px",
            borderRadius: "5px",
            border: "1px solid #a1a0a0",
            fontSize: "14px",
          }}
        />
      </div>

      {/* Fees List */}
      <CardComp
        cardClassName="border-0 rounded-10 px-0 mb-4 shadow3"
        cardBodyClassName="p-0"
      >
        {isLoading || isFetching ? (
          <div className="p-4">
            <TableSkeletonLoader rows={5} columns={1} />
          </div>
        ) : filteredFees.length === 0 ? (
          <div className="p-4 text-center text-muted">
            {searchTerm
              ? "No registration fees found matching your search"
              : "No registration fees available from other branches"}
          </div>
        ) : (
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            {/* Select All Checkbox */}
            <div
              className="px-4 py-3 border-bottom"
              style={{
                backgroundColor: "#f7f7f7",
                position: "sticky",
                top: 0,
                zIndex: 10,
              }}
            >
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="selectAll"
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  style={{ cursor: "pointer" }}
                />
                <label
                  className="form-check-label fw-bold text-black1"
                  htmlFor="selectAll"
                  style={{ cursor: "pointer", fontSize: "14px" }}
                >
                  Select All ({filteredFees.length})
                </label>
              </div>
            </div>

            {/* Fees List */}
            {filteredFees.map((fee) => {
              const isSelected = selectedFees.includes(fee.id);
              return (
                <div
                  key={fee.id}
                  className={`px-4 py-3 border-bottom ${
                    isSelected ? "bg-light" : ""
                  }`}
                  style={{
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                  onClick={() => handleToggleSelection(fee.id)}
                >
                  <div className="d-flex align-items-center">
                    <div className="form-check me-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSelection(fee.id)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="fw-bold text-black1 mb-0" style={{ fontSize: "14px" }}>
                          {fee.name || "N/A"}
                        </h6>
                        <span
                          className="badge rounded-pill"
                          style={{
                            backgroundColor: "var(--color-secondary)",
                            color: "white",
                            fontSize: "12px",
                            padding: "4px 10px",
                          }}
                        >
                          {fee.branch_name || "N/A"}
                        </span>
                      </div>
                      <div className="d-flex flex-wrap gap-3" style={{ fontSize: "12px" }}>
                        {fee.amount && (
                          <span className="text-muted">
                            Amount: {formatMoney(fee.amount)}
                          </span>
                        )}
                        {fee.fee_category_name && (
                          <span className="text-muted">
                            Category: {fee.fee_category_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardComp>

      {/* Action Buttons */}
      <div className="d-flex justify-content-end gap-3">
        <ButtonComp
          btnText="Cancel"
          onClick={handleClose}
          btnClassName="bg-white py-2 px-4 rounded text-black1 border-2"
          style={{
            minWidth: "120px",
            borderColor: "var(--color-primary)",
            color: "var(--color-primary)",
          }}
        />
        <ButtonComp
          btnText={
            isImporting
              ? "Importing..."
              : `Import ${selectedFees.length > 0 ? `(${selectedFees.length})` : ""}`
          }
          onClick={handleImport}
          btnClassName="bg-primary1 py-2 px-4 rounded text-white border-0"
          style={{ minWidth: "150px" }}
          loading={isImporting}
          disabled={selectedFees.length === 0 || isImporting}
        />
      </div>
    </div>
  );
};

export default ImportRegistrationFeesModal;




